/**
 * Cliente de CONTEÚDO server-safe — zero DOM, zero storage, zero Faro.
 *
 * Existe como entrypoint separado por um motivo concreto: o core NÃO roda em
 * Node. O Faro depende de `window`/`document`/`PerformanceObserver`, e é por
 * isso que o plugin Nuxt devolve um stub no-op no servidor. Essa decisão está
 * certa para telemetria e não deve ser desfeita — então o CMS ganha a própria
 * porta em vez de tentar tornar o core isomórfico.
 *
 * Só `fetch` e os DTOs. Roda em Nitro, edge worker e script Node avulso.
 *
 * Serve APENAS o eixo endereçado, que é determinístico. Decisão de anúncio não
 * passa por aqui: ela é por visitante e depende do cookie, que não existe num
 * SSR frio (ver RFC §6.4).
 */

import { MONTILLA_HOSTS, type Environment } from '../constants'

export { MONTILLA_HOSTS }

/**
 * Host do rum-api para o ambiente. Vive aqui, e não no `.`, porque este é o
 * entry server-safe: quem monta conteúdo no servidor precisa do host e não
 * pode importar o entry principal, que arrasta o Faro.
 *
 * Existe para que ninguém copie `MONTILLA_HOSTS` à mão de novo — foi o que o
 * pacote nuxt fez, e a cópia divergiu do mapa de verdade em produção.
 */
export function resolveMontillaHost(environment: Environment | undefined): string {
  return MONTILLA_HOSTS[environment ?? 'production'] ?? MONTILLA_HOSTS.production
}

export type { Environment }

/** Documento do ProseMirror, estrutural — o cliente não carrega o Tiptap. */
export interface CmsRichtextDoc {
  type: 'doc'
  content?: unknown[]
}

export interface CmsEntryAddress {
  collection: string
  slug: string
  locale: string
}

export interface CmsEntry {
  id: string
  type: string
  name: string
  address: CmsEntryAddress
  publishedAt: string | null
  /** Já renderizado para `richtext`/`html`; `null` nos demais tipos. */
  html: string | null
  /** Conteúdo cru, para montar a própria UI. */
  content: Record<string, unknown>
}

export interface CmsList {
  collection: string
  locale: string
  total: number
  entries: CmsEntry[]
}

export interface CmsClientOptions {
  /** Origem do rum-api, ex.: `https://t.quero.com`. */
  baseUrl: string
  /** Locale padrão das buscas. Default `pt-BR`. */
  locale?: string
  /** Timeout por requisição, em ms. Default 5000. */
  timeoutMs?: number
  /** Injetável para teste e para runtimes com fetch próprio. */
  fetch?: typeof globalThis.fetch
}

export interface CmsQuery {
  locale?: string
  limit?: number
  offset?: number
}

/** Miss de endereço. Separado de erro de rede: um vira 404, o outro vira 500. */
export class CmsNotFoundError extends Error {
  constructor(readonly path: string) {
    super(`conteúdo não encontrado: ${path}`)
    this.name = 'CmsNotFoundError'
  }
}

export class CmsRequestError extends Error {
  constructor(readonly status: number, readonly path: string) {
    super(`falha ao buscar ${path}: HTTP ${status}`)
    this.name = 'CmsRequestError'
  }
}

const DEFAULT_LOCALE = 'pt-BR'
const DEFAULT_TIMEOUT_MS = 5000

export interface CmsClient {
  /**
   * Uma entrada por endereço `collection/slug`.
   *
   * LANÇA `CmsNotFoundError` no miss, e é deliberado: quem chama isto está
   * montando uma página, e devolver `null` em silêncio vira soft 404. Um slot
   * é que colapsa sem barulho — ele é um buraco opcional no layout.
   */
  get(path: string, query?: Pick<CmsQuery, 'locale'>): Promise<CmsEntry>
  /** Como `get`, mas devolve `null` no miss — para blocos realmente opcionais. */
  find(path: string, query?: Pick<CmsQuery, 'locale'>): Promise<CmsEntry | null>
  list(collection: string, query?: CmsQuery): Promise<CmsList>
}

export function createCmsClient(options: CmsClientOptions): CmsClient {
  const base = options.baseUrl.replace(/\/+$/, '')
  const defaultLocale = options.locale ?? DEFAULT_LOCALE
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const doFetch = options.fetch ?? globalThis.fetch

  async function request<T>(path: string, params: Record<string, string | undefined>): Promise<T | null> {
    const url = new URL(`${base}${path}`)
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) url.searchParams.set(k, v)
    }

    // `AbortSignal.timeout` em vez de um controller manual: sem timeout, um
    // rum-api lento vira uma página de SSR pendurada, e o Nitro segura a
    // conexão até o cliente desistir.
    const res = await doFetch(url.toString(), {
      signal: AbortSignal.timeout(timeoutMs),
      headers: { accept: 'application/json' },
    })

    if (res.status === 404) return null
    if (!res.ok) throw new CmsRequestError(res.status, path)
    return (await res.json()) as T
  }

  function splitPath(path: string): { collection: string, slug: string } {
    const clean = path.replace(/^\/+/, '')
    const i = clean.indexOf('/')
    if (i <= 0 || i === clean.length - 1) {
      throw new TypeError(`endereço inválido "${path}" — use "colecao/slug"`)
    }
    return { collection: clean.slice(0, i), slug: clean.slice(i + 1) }
  }

  async function find(path: string, query: Pick<CmsQuery, 'locale'> = {}) {
    const { collection, slug } = splitPath(path)
    return request<CmsEntry>(
      `/cms/${encodeURIComponent(collection)}/${encodeURIComponent(slug)}`,
      { locale: query.locale ?? defaultLocale },
    )
  }

  return {
    find,
    async get(path, query = {}) {
      const entry = await find(path, query)
      if (!entry) throw new CmsNotFoundError(path)
      return entry
    },
    async list(collection, query = {}) {
      const out = await request<CmsList>(`/cms/${encodeURIComponent(collection)}`, {
        locale: query.locale ?? defaultLocale,
        limit: query.limit === undefined ? undefined : String(query.limit),
        offset: query.offset === undefined ? undefined : String(query.offset),
      })
      // Coleção inexistente e coleção vazia são a mesma coisa para quem LISTA:
      // nos dois casos não há o que renderizar, e uma lista vazia é mais fácil
      // de tratar do que um throw no meio de um `Promise.all` de página.
      return out ?? { collection, locale: query.locale ?? defaultLocale, total: 0, entries: [] }
    },
  }
}
